import * as d3 from "d3";
import { useEffect, useRef, useState } from "react";
import NibiousChartLine, { ILineData, Point } from './components/Area';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ILLRPoint, IAgeDBHMap } from 'services/ApplicationConfig';
import { NumberValue } from "d3";
import { simpleInterpolation } from 'utils/reliability_index';

interface Props {
  //  data: ILineData[],
  //  labels: {
  //    x: string,
  //    y: string
  //  }
  //  avgPoint: Point
  id: string;
  rrParams: {
    start: number,
    end: number,
    step: number
  },
  beta: number[], // Array has to be the same size as many as rrParams dictates
  dbh: Map<number, { lrr: number, canopy_cover: number }[]>,
  design_value: number,
  mark: {
    lrr: number,
    canopy_cover: number
  },
  metrics: {
    rr_thresholds: {
      median: number,
      perc1: number,
      perc10: number
    }
  }
}

const original_width = 1000;
const original_height = 500;
const margin = { top: 40, right: 160, bottom: 50, left: 50 };
const width = original_width - margin.left - margin.right;
const height = original_height - margin.top - margin.bottom;

const data_width = 60;
const data_height = 96;
const scale_height = -1 * height / data_height;
const scale_width = width / data_width;

export default function CanopyCoverChart(props: Props) {

  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);
  const axisRightRef = useRef<SVGGElement>(null);
  const shapeRef = useRef<SVGGElement>(null);
  const legendRef = useRef<SVGGElement>(null);
  const textOverlayRef = useRef<SVGGElement>(null);
  const clipPathRef = useRef<SVGGElement>(null);

  const [y_max, set_y_max] = useState(props.rrParams.end / 1000);

  const WASMApplicationState = useSelector((state: RootState) => state.WASMApplication);

  const y_min = props.rrParams.start / 1000; //Convert to kN/m

  const x_min = 0;
  const x_max = 100;

  const scaleX = d3.scaleLinear().domain([x_min, x_max]).range([0, width]);
  const scaleY = d3
    .scaleLinear()
    .domain([y_max, y_min])
    .range([0, height]);
  /*
    useEffect(() => {
  
      // Take the first value of the array of each array...
      let y_max = 0;
      for (var k of props.dbh.keys()) {
  
        const v = props.dbh.get(k);
        if (v === undefined) continue;
  
        y_max = Math.max(y_max, v[v.length - 1].lrr);
      }
  
      set_y_max((y_max * 1.10) / 1000);
    }, [props.dbh]);*/

  useEffect(() => {

    if (axisBottomRef.current) {

      d3.select(axisBottomRef.current).selectAll("*").remove();
      d3.select(axisBottomRef.current).call(d3.axisBottom(scaleX));
      d3.select(axisBottomRef.current).append("text")
        .attr("x", width / 2)
        .attr("y", margin.bottom - 15)
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1.5em')
        .attr("fill", "currentColor")
        .text("Canopy Cover (%)");
    }


    if (axisRightRef.current) {
      d3.select(axisRightRef.current).selectAll("*").remove();
      d3.select(axisRightRef.current).call(d3.axisRight(scaleY));
    }



    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).selectAll("*").remove();
      d3.select(axisLeftRef.current).call(d3.axisLeft(scaleY));
      d3.select(axisLeftRef.current).append("text")
        .attr("x", -height / 2)
        .attr("y", -margin.left + 15)
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1.5em')
        .attr("transform", "rotate(-90)")
        .attr("fill", "currentColor")
        .text("Root Reinforcement (kN/m)");


      // Let's add the horizontal lines here
      for (let t of scaleY.ticks()) {
        d3.select(axisLeftRef.current).append("line")
          .attr("x1", scaleX(x_min))
          .attr("y1", scaleY(t))
          .attr("x2", scaleX(x_max))
          .attr("y2", scaleY(t))
          .attr("stroke", "black")
          .attr("stroke-width", .5);

      }
    }


    const perc1_color = "coral";
    const perc10_color = "chocolate";
    const median_color = "red";


    if (shapeRef.current) {

      d3.select(shapeRef.current).selectAll("*").remove();


      console.log(`Metrics: ${props.metrics}`);

      // Add beta red line if necessary
      var median;
      let off_screen = false;
      if (props.metrics.rr_thresholds.median === -9999) {
        median = y_max;
        off_screen = true;
      } else if (props.metrics.rr_thresholds.median === -1111) {
        median = 0;
        off_screen = true;
      } else {
        median = props.metrics.rr_thresholds.median / 1000;
      }
      d3.select(shapeRef.current).append("line")
        .attr("x1", scaleX(x_min))
        .attr("y1", scaleY(median))
        .attr("x2", scaleX(x_max))
        .attr("y2", scaleY(median))
        .attr("stroke", median_color)
        .attr("stroke-dasharray", "3 3")
        .attr("stroke-width", 4);

      if (off_screen) {
        d3.select(shapeRef.current).append("text")
          .attr("x", margin.left + 30)
          .attr("y", scaleY(median) - 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '1em')
          .attr("fill", "black")
          .text(props.metrics.rr_thresholds.median === -1111 ? "50% < 0.001 kN/m" : "50% > 20 kN/m");
      }

      if (props.dbh.size === 0) {
        d3.select(shapeRef.current).append("text")
          .attr("x", width / 2)
          .attr("y", height / 2)
          .attr('text-anchor', 'middle')
          .attr('font-size', '1.5em')
          .style("font-weight", 900)
          .attr("fill", "black")
          .text("Canopy cover data not available");

      }


      var val;
      off_screen = false;

      if (props.metrics.rr_thresholds.perc1 === -9999) {
        val = y_max;
        off_screen = true;
      } else if (props.metrics.rr_thresholds.perc1 === -1111) {
        val = 0;
        off_screen = true;
      } else {
        val = props.metrics.rr_thresholds.perc1 / 1000;
      }



      d3.select(shapeRef.current).append("line")
        .attr("x1", scaleX(x_min))
        .attr("y1", scaleY(val))
        .attr("x2", scaleX(x_max))
        .attr("y2", scaleY(val))
        .attr("stroke", perc1_color)
        .attr("stroke-dasharray", "5 10")
        .attr("stroke-width", 4);

      if (off_screen) {
        d3.select(shapeRef.current).append("text")
          .attr("x", margin.left + 420)
          .attr("y", scaleY(val) - 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '1em')
          .attr("fill", "black")
          .text(props.metrics.rr_thresholds.perc1 === -1111 ? "1% < 0.001 kN/m" : "1% > 20 kN/m");
      }


      off_screen = false;
      if (props.metrics.rr_thresholds.perc10 === -9999) {
        val = y_max;
        off_screen = true;
      } else if (props.metrics.rr_thresholds.perc10 === -1111) {
        val = 0;
        off_screen = true;
      } else {
        val = props.metrics.rr_thresholds.perc10 / 1000;
      }

      d3.select(shapeRef.current).append("line")
        .attr("x1", scaleX(x_min))
        .attr("y1", scaleY(val))
        .attr("x2", scaleX(x_max))
        .attr("y2", scaleY(val))
        .attr("stroke", perc10_color)
        .attr("stroke-dasharray", "10 5")
        .attr("stroke-width", 4);

      if (off_screen) {
        d3.select(shapeRef.current).append("text")
          .attr("x", margin.left + 230)
          .attr("y", scaleY(val) - 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '1em')
          .attr("fill", "black")
          .text(props.metrics.rr_thresholds.perc10 === -1111 ? "10% < 0.001 kN/m" : "10% > 20 kN/m");
      }


      d3.select(textOverlayRef.current).selectAll("*").remove();
      if (props.mark.lrr !== -1 && props.mark.canopy_cover !== -1) {
        console.log(props.mark)
        d3.select(textOverlayRef.current)
          .append("path")
          .attr("transform", `translate(${scaleX(props.mark.canopy_cover)},  ${scaleY(props.mark.lrr)})`)
          .attr("d", d3.symbol(d3.symbolWye, 64 * 3))
          .style("fill", '#9f3ec1')
      }

    }

    if (legendRef.current && props.dbh.size !== 0) {
      d3.select(legendRef.current).selectAll("*").remove();
      d3.select(legendRef.current)
        .append("text")
        .text(function(d) { return "DBH (cm)" })
        .style("fill", function(d) { return 'black' })
        .style("font-size", "1em")
        .style("font-weight", "bold")

      var last_index = 0;
      Array.from(props.dbh.entries()).map((data, i) => {
        // Convert to points

        d3.select(legendRef.current)
          .append("line")
          .attr("x1", 0)
          .attr("y1", (i + 1) * 20 - 5)
          .attr("x2", 10)
          .attr("y2", (i + 1) * 20 - 5)
          .style("stroke", colors[i])
          .style("stroke-width", "4px")



        d3.select(legendRef.current)
          .append("text")
          .text(function(d) { return `${Math.floor(data[0] * 100)}` })
          .attr("y", (i + 1) * 20)
          .attr("x", 12)
          .style("fill", function(d) { return 'black' })
          .style("font-size", "1em")

        last_index++;

      })

      d3.select(legendRef.current)
        .append("line")
        .attr("x1", 0)
        .attr("y1", last_index * 20 + 20)
        .attr("x2", 30)
        .attr("y2", last_index * 20 + 20)
        .style("stroke", "black")
        .style("stroke-width", "1px")

      d3.select(legendRef.current)
        .append("circle")
        .attr("cx", 20)
        .attr("cy", last_index * 20 + 20)
        .attr("clip-path", "url(#clip)")
        .attr("r", 2)
        .style("fill", "black")


      d3.select(legendRef.current)
        .append("text")
        .text(function(d) { return "Mean values"; })
        .attr("y", last_index * 20 + 25)
        .attr("x", 32)
        .style("fill", function(d) { return 'black' })
        .style("font-size", "1em")

      //ean value (solid line) and design value (dash line)
      last_index++;


      d3.select(legendRef.current)
        .append("line")
        .attr("x1", 0)
        .attr("y1", last_index * 20 + 20)
        .attr("x2", 30)
        .attr("y2", last_index * 20 + 20)
        .style("stroke", "black")
        .style("stroke-width", "1px")
        .style("stroke-dasharray", ("10, 5"))

      d3.select(legendRef.current)
        .append("circle")
        .attr("cx", 20)
        .attr("cy", last_index * 20 + 20)
        .attr("clip-path", "url(#clip)")
        .attr("r", 2)
        .style("fill", "black")

      d3.select(legendRef.current)
        .append("text")
        .text(function(d) { return "Design values"; })
        .attr("y", last_index * 20 + 25)
        .attr("x", 32)
        .style("fill", function(d) { return 'black' })
        .style("font-size", "1em")


      last_index++;


      // Legend for the landslide probability
      d3.select(legendRef.current)
        .append("text")
        .text(function(d) { return "Needed level for X%" })
        .style("fill", function(d) { return 'black' })
        .attr("y", (last_index) * 20 + 40)
        .attr("x", 0)
        .style("font-size", "1em")
        .style("font-weight", "bold")
      d3.select(legendRef.current)
        .append("text")
        .text(function(d) { return "landslide probability" })
        .style("fill", function(d) { return 'black' })
        .attr("y", (last_index) * 20 + 60)
        .attr("x", 0)
        .style("font-size", "1em")
        .style("font-weight", "bold")


      var starting_loc = (last_index) * 20 + 60;
      [["1%", perc1_color, "5 10"], ["10%", perc10_color, "10 5"], ["50%", median_color, "3 3"]].map((data, i) => {
        // Convert to points

        d3.select(legendRef.current)
          .append("line")
          .attr("x1", 0)
          .attr("y1", starting_loc + (i + 1) * 20 - 5)
          .attr("x2", 30)
          .attr("y2", starting_loc + (i + 1) * 20 - 5)
          .style("stroke", data[1])
          .attr("stroke-dasharray", data[2])
          .style("stroke-width", "4px")

        d3.select(legendRef.current)
          .append("text")
          .text(function(d) { return data[0] })
          .attr("y", starting_loc + (i + 1) * 20)
          .attr("x", 32)
          .style("fill", function(d) { return 'black' })
          .style("font-size", "1em")

      })




      /*
          // Add the reliability index line
             d3.select(legendRef.current)
                .append("line")
                .attr("x1", 0)
                .attr("y1", (last_index + 1) * 20 - 5)
                .attr("x2", 10)
                .attr("y2", (last_index + 1) * 20 - 5)
                .style("stroke", interpolated_beta_line_color)
                .style("stroke-width", "4px")
      
      
      
              d3.select(legendRef.current)
                .append("text")
                .text(function(d) { return "Reliability Index = 0" })
                .attr("y", (last_index + 1) * 20)
                .attr("x", 12)
                .style("fill", function(d) { return 'black' })
                .style("font-size", 10)
      */
    }

    if (clipPathRef.current) {
      d3.select(clipPathRef.current).append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("width", width)
        .attr("height", height);
    }
  }, [props.rrParams, props.beta, props.dbh, scaleY, props.mark]);

  const colors = ["purple", "blue", "green", "gold", "orange", "brown"];

  return (
    <>
      <svg
        style={{
          maxHeight: "60vh",
          marginRight: "0px",
          marginLeft: "0px",
          marginBottom: `${margin.bottom}px`,
          maxWidth: "100%"
        }}
        viewBox={`0 0 ${original_width} ${original_height}`}
        id={props.id}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g transform={`scale(.86, 1)`}>
            <g ref={clipPathRef} />
            <g ref={axisBottomRef} transform={`translate(0, ${height})`} />
            <g ref={axisLeftRef} />
            <g ref={axisRightRef} transform={`translate(${width},0)`} />

            {
              Array.from(props.dbh.entries()).map((d, i) => {
                var p: Point[] = d[1].map(v => { return [v.canopy_cover * 100, v.lrr / 1000] });
                return <NibiousChartLine key={i + "-dbh-chartline"} data={{ name: `DBH = ${(d[0] / 100).toPrecision(1)}`, color: colors[i], points: p, design_value: props.design_value }} scaleX={scaleX} scaleY={scaleY} />
              })
            }


            <g ref={shapeRef} />
            <g ref={legendRef} transform={`translate(${width + 30}, 50)`} />
            <g ref={textOverlayRef} />
          </g>
        </g>
      </svg>
    </>
  );
}
